import React, { useMemo, useState } from 'react';
import { useStores, setBudgetStatus, put, remove } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import AttachmentLinks from '../components/AttachmentLinks';
import {
  Banknote, Wallet, Clock, CheckCircle2, Users, Inbox,
  BadgeCheck, Ban, RotateCcw, Loader2, Search, FileText, Trash2, X, Check, Download, Pencil,
} from 'lucide-react';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import { createZip, dataUrlToBytes } from '../utils/zip';

// Filesystem-safe filename fragment.
const safe = (s) => (s || '')
  .replace(/[^a-zA-Z0-9._-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80) || 'file';

const statusClass = {
  pending: 'sh-badge-warning',
  approved: 'sh-badge-info',
  paid: 'sh-badge-success',
  disputed: 'sh-badge-danger',
  cancelled: 'sh-badge-outline',
};

const STATUS_FILTERS = ['all', 'pending', 'approved', 'paid', 'disputed', 'cancelled'];

const AI_STORES = ['budget', 'attendees'];

// The claimed amount, editable in place by finance — an employee mistypes a
// figure or the receipt total differs, and the ledger has to be correctable
// without deleting and re-filing the invoice. Shared by the card and table
// views; every saved change stamps an audit trail on the row.
function AmountCell({ row, editing, draft, saving, onDraft, onStart, onSave, onCancel, valueClass = 'text-sm' }) {
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-500 pointer-events-none">₹</span>
          <input
            autoFocus
            type="number"
            min="0"
            step="0.01"
            value={draft}
            disabled={saving}
            aria-label={`Amount for ${row.title}`}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onSave(); }
              if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
            }}
            className="w-28 bg-white border border-slate-800 rounded-lg pl-5 pr-2 py-1 text-sm font-mono font-bold outline-none disabled:opacity-50"
          />
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          title="Save amount (Enter)"
          aria-label="Save amount"
          className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition disabled:opacity-50"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          title="Cancel (Esc)"
          aria-label="Cancel amount edit"
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition disabled:opacity-50"
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className={`font-mono font-bold text-slate-900 ${valueClass}`}>
        ₹{Number(row.amount || 0).toLocaleString('en-IN')}
      </span>
      <button
        type="button"
        onClick={onStart}
        disabled={saving}
        title="Edit amount"
        aria-label={`Edit amount for ${row.title}`}
        className="p-1 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition disabled:opacity-50"
      >
        <Pencil size={12} />
      </button>
      {row.originalAmount != null && (
        <span
          title={
            `Originally ₹${Number(row.originalAmount || 0).toLocaleString('en-IN')} · edited by ${row.amountEditedBy || 'admin'}` +
            (row.amountEditedAt ? ` on ${new Date(row.amountEditedAt).toLocaleString('en-IN')}` : '')
          }
          className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
        >
          edited
        </span>
      )}
    </div>
  );
}

// Shared row-action buttons — used by both the mobile card list and the
// tablet/desktop table so the two views can never drift out of sync.
function InvoiceActions({ row, busy, onApprove, onPay, onDispute, onRevert, onDelete }) {
  const isPaid = row.status === 'paid';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
      {!isPaid && (
        <>
          {row.status === 'pending' && (
            <button
              type="button"
              disabled={busy}
              onClick={onApprove}
              title="Approve for payout"
              className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 hover:bg-sky-50 px-2 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50"
            >
              <BadgeCheck size={13} /> Approve
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onPay}
            title="Mark as paid"
            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50"
          >
            <Banknote size={13} /> Mark Paid
          </button>
          {row.status !== 'disputed' && (
            <button
              type="button"
              disabled={busy}
              onClick={onDispute}
              title="Flag as disputed"
              className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50"
            >
              <Ban size={13} /> Dispute
            </button>
          )}
        </>
      )}
      {row.status !== 'pending' && (
        <button
          type="button"
          disabled={busy}
          onClick={onRevert}
          title="Revert to pending"
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50"
        >
          <RotateCcw size={13} /> Revert
        </button>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        title="Delete invoice"
        className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-50"
      >
        <Trash2 size={13} /> Delete
      </button>
    </div>
  );
}

// Mobile presentation of a single invoice — a table row's worth of columns
// (employee, expense detail, amount, status, receipt, filed date, actions)
// doesn't fit a phone screen even with horizontal scroll, so it's laid out
// as a stacked card instead.
function InvoiceCard({ row, name, busy, amount, onApprove, onPay, onDispute, onRevert, onDelete }) {
  const isPaid = row.status === 'paid';
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{name}</div>
          {row.submittedByEmail && <div className="text-xs text-slate-500 truncate">{row.submittedByEmail}</div>}
        </div>
        <div className="shrink-0 text-right">
          <span className={`sh-badge ${statusClass[row.status] || 'sh-badge-outline'}`}>{row.status || 'pending'}</span>
          {isPaid && row.paidAt && (
            <div className="text-[10px] text-slate-400 mt-1 font-mono">{new Date(row.paidAt).toLocaleDateString('en-IN')}</div>
          )}
        </div>
      </div>

      <div>
        <div className="font-semibold text-slate-900">{row.title}</div>
        <div className="text-xs text-slate-500">{row.category}{row.vendor ? ` · ${row.vendor}` : ''}</div>
        {row.notes && (
          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-600 bg-slate-50 border border-slate-150 rounded-md px-2 py-1.5">
            <FileText size={12} className="text-slate-400 shrink-0 mt-0.5" />
            <span className="whitespace-pre-wrap break-words">{row.notes}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        {amount}
        <span className="text-xs text-slate-500 font-mono">{row.date || '—'}</span>
      </div>

      <AttachmentLinks attachments={row.attachments} />

      <div className="pt-2 border-t border-slate-100">
        <InvoiceActions row={row} busy={busy} onApprove={onApprove} onPay={onPay} onDispute={onDispute} onRevert={onRevert} onDelete={onDelete} />
      </div>
    </div>
  );
}

export default function AdminInvoices({ currentUser = {} }) {
  const { budget, attendees, loading } = useStores(AI_STORES);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [zipping, setZipping] = useState(false);
  // { id, draft } while an invoice amount is being corrected in place.
  const [amountEdit, setAmountEdit] = useState(null);

  // Every reimbursement invoice from every employee (newest first — the store
  // is already returned in updated_at DESC order by the backend).
  const invoices = useMemo(
    () => budget.filter((row) => row.reimbursement),
    [budget]
  );

  const nameFor = (row) => {
    if (row.submittedByName) return row.submittedByName;
    const match = attendees.find(
      (a) => (a.email || '').toLowerCase() === (row.submittedByEmail || '').toLowerCase()
    );
    return match?.name || row.submittedByEmail || 'Unknown';
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invoices.filter((row) => {
      if (statusFilter !== 'all' && (row.status || 'pending') !== statusFilter) return false;
      if (!q) return true;
      const haystack = [row.title, row.vendor, row.category, row.submittedByEmail, nameFor(row)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, statusFilter, query, attendees]);

  // ── Per-person payout aggregation (single source of truth) ─────────────────
  // Calculation rules — kept deliberately explicit so the numbers are auditable:
  //   • amount            → parseFloat(row.amount) || 0  (values are stored as strings)
  //   • status            → row.status || 'pending'      (default un-triaged rows to pending)
  //   • per-status buckets: pending / approved / paid / disputed / cancelled
  //   • "To Pay" (owed)   = pending + approved
  //                         (excludes: paid = already reimbursed, cancelled = void,
  //                          disputed = contested and NOT payable until resolved)
  // The header totals below are derived from this map, so the grid always sums
  // to the headline figures.
  const STATUS_KEYS = ['pending', 'approved', 'paid', 'disputed', 'cancelled'];

  const byPerson = useMemo(() => {
    const map = new Map();
    for (const row of invoices) {
      const key = (row.submittedByEmail || '').toLowerCase() || `__unknown__:${row.id}`;
      const amount = parseFloat(row.amount) || 0;
      let status = row.status || 'pending';
      if (!STATUS_KEYS.includes(status)) status = 'pending';
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: nameFor(row),
          email: row.submittedByEmail || '',
          count: 0,
          pending: 0, approved: 0, paid: 0, disputed: 0, cancelled: 0,
        });
      }
      const p = map.get(key);
      p.count += 1;
      p[status] += amount;
    }
    return Array.from(map.values())
      .map((p) => ({ ...p, toPay: p.pending + p.approved }))
      // Whoever is owed the most first; ties broken by unpaid count then name.
      .sort((a, b) => b.toPay - a.toPay || (b.pending + b.approved) - (a.pending + a.approved) || a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, attendees]);

  // Headline totals derived from the per-person map so they reconcile exactly.
  const outstanding = byPerson.reduce((s, p) => s + p.toPay, 0);
  const paidOut = byPerson.reduce((s, p) => s + p.paid, 0);
  const claimants = byPerson.length;
  const awaitingAction = invoices.filter((r) => ['pending', 'approved', 'disputed'].includes(r.status || 'pending')).length;

  // Receipts available in the CURRENT (filtered) view — the zip respects filters.
  const receiptCount = filtered.reduce(
    (n, r) => n + (r.attachments || []).filter((a) => a && a.dataUrl).length,
    0
  );

  const changeStatus = async (row, status, { needsConfirm } = {}) => {
    if (busyId) return;
    if (needsConfirm) {
      const ok = await confirm(
        `Mark "${row.title}" (₹${Number(row.amount || 0).toLocaleString('en-IN')}) from ${nameFor(row)} as PAID? ` +
        'The employee will see it as reimbursed.',
        'Confirm Payout'
      );
      if (!ok) return;
    }
    setBusyId(row.id);
    try {
      await setBudgetStatus(row.id, status, currentUser.email);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      const label = status === 'paid' ? 'marked as paid' : `set to ${status}`;
      toast.success(`Invoice ${label}.`);
    } catch (e) {
      toast.error(e.message || 'Failed to update invoice status.');
    } finally {
      setBusyId(null);
    }
  };

  // ── Amount correction ──────────────────────────────────────────────────────
  const startAmountEdit = (row) => {
    if (busyId) return;
    setAmountEdit({ id: row.id, draft: String(row.amount ?? '') });
  };

  const saveAmount = async (row) => {
    if (busyId) return;
    const next = parseFloat(amountEdit?.draft);
    if (!Number.isFinite(next) || next < 0) {
      toast.error('Enter a valid amount of 0 or more.');
      return;
    }
    const prev = parseFloat(row.amount) || 0;
    if (next === prev) { setAmountEdit(null); return; }

    // A paid invoice has already been reimbursed, so changing its figure
    // rewrites a settled payout — make that deliberate.
    if (row.status === 'paid') {
      const ok = await confirm(
        `"${row.title}" is already marked PAID. Change the amount from ₹${prev.toLocaleString('en-IN')} ` +
        `to ₹${next.toLocaleString('en-IN')}? ${nameFor(row)} will see the corrected figure.`,
        'Edit a Paid Invoice'
      );
      if (!ok) return;
    }

    setBusyId(row.id);
    try {
      await put('budget', {
        ...row,
        amount: String(next),
        // Reimbursement rows are filed with estimated === amount; keep them in
        // sync so the budget ledger doesn't flag a correction as overspend.
        estimated: String(next),
        // Audit trail — the first edit preserves what the employee claimed.
        originalAmount: row.originalAmount ?? String(prev),
        amountEditedBy: currentUser.email || 'admin',
        amountEditedAt: new Date().toISOString(),
      });
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      setAmountEdit(null);
      toast.success(`Amount updated to ₹${next.toLocaleString('en-IN')}.`);
    } catch (e) {
      toast.error(e.message || 'Failed to update the amount.');
    } finally {
      setBusyId(null);
    }
  };

  // Props for the shared AmountCell — identical in the card and table views.
  const amountProps = (row) => ({
    row,
    editing: amountEdit?.id === row.id,
    draft: amountEdit?.id === row.id ? amountEdit.draft : '',
    saving: busyId === row.id,
    onDraft: (value) => setAmountEdit({ id: row.id, draft: value }),
    onStart: () => startAmountEdit(row),
    onSave: () => saveAmount(row),
    onCancel: () => setAmountEdit(null),
  });

  const del = async (row) => {
    if (busyId) return;
    const ok = await confirm(
      `Permanently delete "${row.title}" (₹${Number(row.amount || 0).toLocaleString('en-IN')}) submitted by ${nameFor(row)}? ` +
      'This removes it from the ledger for both finance and the employee.',
      'Delete Invoice'
    );
    if (!ok) return;
    setBusyId(row.id);
    try {
      await remove('budget', row.id);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success('Invoice deleted.');
    } catch (e) {
      toast.error(e.message || 'Failed to delete invoice.');
    } finally {
      setBusyId(null);
    }
  };

  const downloadReceipts = async () => {
    if (zipping) return;
    // Build the file list from the currently filtered invoices, grouping each
    // person's receipts into their own folder and de-duplicating names.
    const files = [];
    const used = new Set();
    filtered.forEach((row) => {
      const folder = safe(nameFor(row));
      (row.attachments || []).forEach((att, i) => {
        if (!att || !att.dataUrl) return;
        const base = safe(att.name || `${row.title || 'receipt'}-${i + 1}`);
        let name = `${folder}/${base}`;
        let n = 2;
        while (used.has(name)) {
          const dot = base.lastIndexOf('.');
          const stem = dot > 0 ? base.slice(0, dot) : base;
          const ext = dot > 0 ? base.slice(dot) : '';
          name = `${folder}/${stem}-${n}${ext}`;
          n += 1;
        }
        used.add(name);
        try {
          files.push({ name, data: dataUrlToBytes(att.dataUrl) });
        } catch {
          /* skip an unreadable attachment rather than failing the whole zip */
        }
      });
    });

    if (!files.length) {
      toast.error('No receipts to download in the current view.');
      return;
    }

    setZipping(true);
    try {
      const blob = createZip(files);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reimbursement-receipts-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${files.length} receipt${files.length > 1 ? 's' : ''}.`);
    } catch (e) {
      toast.error('Failed to build the zip archive.');
    } finally {
      setZipping(false);
    }
  };

  if (loading) return <PageLoader label="Loading reimbursements…" sublabel="Fetching every employee invoice" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Banknote style={{ color: 'var(--accent)' }} size={24} />
            <span>Employee Reimbursements</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Review and pay expense invoices submitted by the team. Marking an invoice paid reflects instantly to the employee.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadReceipts}
          disabled={zipping || receiptCount === 0}
          title={receiptCount === 0 ? 'No receipts in the current view' : 'Download all receipts in the current view as a zip'}
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {zipping ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          <span>{zipping ? 'Zipping…' : `Download Receipts${receiptCount ? ` (${receiptCount})` : ''}`}</span>
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sh-stat-card border-l-4 border-amber-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Outstanding to Pay</span>
            <Wallet className="text-amber-500" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">₹{outstanding.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500">Pending + approved (excludes disputed)</p>
        </div>

        <div className="sh-stat-card border-l-4 border-sky-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Awaiting Action</span>
            <Clock className="text-sky-500" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{awaitingAction} items</div>
          <p className="text-xs text-slate-500">Pending, approved or disputed</p>
        </div>

        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Paid Out</span>
            <CheckCircle2 className="text-emerald-500" size={16} />
          </div>
          <div className="sh-stat-value text-emerald-600 font-display">₹{paidOut.toLocaleString('en-IN')}</div>
          <p className="text-xs text-slate-500">Reimbursed to employees</p>
        </div>

        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Claimants</span>
            <Users className="text-slate-500" size={16} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{claimants}</div>
          <p className="text-xs text-slate-500">{invoices.length} invoices in total</p>
        </div>
      </div>

      {/* Per-person payout summary — "who do I pay, and how much" */}
      {byPerson.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Payout Summary by Person</h3>
            <span className="text-xs text-slate-500">Sorted by amount owed · click a card to filter</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byPerson.map((p) => {
              const token = (p.email || p.name || '').toLowerCase();
              const active = token !== '' && query.trim().toLowerCase() === token;
              return (
              <button
                key={p.key}
                type="button"
                aria-pressed={active}
                onClick={() => setQuery(active ? '' : (p.email || p.name))}
                style={active ? { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent)' } : undefined}
                className={`text-left border rounded-xl p-4 transition ${active ? 'bg-amber-50/50 border-transparent' : 'border-slate-200 bg-slate-50/40 hover:border-slate-400 hover:shadow-sm'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    {p.email && <div className="text-xs text-slate-500 truncate">{p.email}</div>}
                  </div>
                  {active ? (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-white rounded-full px-2 py-0.5"
                      style={{ backgroundColor: 'var(--accent)' }}
                    >
                      <Check size={11} /> Filtering
                    </span>
                  ) : (
                    <span className="shrink-0 text-[10px] font-bold text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                      {p.count} inv
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-end justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">To Pay</span>
                  {p.toPay > 0 ? (
                    <span className="text-xl font-bold font-display" style={{ color: 'var(--accent)' }}>
                      ₹{p.toPay.toLocaleString('en-IN')}
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-emerald-600 font-display">All settled</span>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-150 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                  <span className="text-slate-500">
                    Pending <span className="font-semibold text-amber-600">₹{p.pending.toLocaleString('en-IN')}</span>
                  </span>
                  <span className="text-slate-500">
                    Approved <span className="font-semibold text-sky-600">₹{p.approved.toLocaleString('en-IN')}</span>
                  </span>
                  <span className="text-slate-500">
                    Paid <span className="font-semibold text-emerald-600">₹{p.paid.toLocaleString('en-IN')}</span>
                  </span>
                  {p.disputed > 0 && (
                    <span className="text-slate-500">
                      Disputed <span className="font-semibold text-red-600">₹{p.disputed.toLocaleString('en-IN')}</span>
                    </span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 select-none">
          {STATUS_FILTERS.map((s) => {
            const count = s === 'all' ? invoices.length : invoices.filter((r) => (r.status || 'pending') === s).length;
            return (
              <button
                key={s}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition shrink-0 capitalize ${statusFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'All' : s} ({count})
              </button>
            );
          })}
        </div>
        <div className="relative shrink-0 sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employee, title, vendor…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-8 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              title="Clear search"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md p-1 transition"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Invoice list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <Inbox className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No invoices match</h3>
          <p className="text-sm text-slate-500 mt-1">
            {invoices.length === 0 ? 'No employee has submitted a reimbursement invoice yet.' : 'Try a different status filter or search term.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-sm">
          {/* Mobile: one card per invoice — a 7-column table doesn't fit a phone */}
          <div className="sm:hidden divide-y divide-slate-100">
            {filtered.map((row) => (
              <InvoiceCard
                key={row.id}
                row={row}
                name={nameFor(row)}
                busy={busyId === row.id}
                amount={<AmountCell {...amountProps(row)} valueClass="text-base" />}
                onApprove={() => changeStatus(row, 'approved')}
                onPay={() => changeStatus(row, 'paid', { needsConfirm: true })}
                onDispute={() => changeStatus(row, 'disputed')}
                onRevert={() => changeStatus(row, 'pending')}
                onDelete={() => del(row)}
              />
            ))}
          </div>

          {/* Tablet/desktop: full table */}
          <div className="hidden sm:block table-wrap">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Employee</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Expense Detail</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Amount</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left w-24">Status</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Receipt</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Filed</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => {
                  const busy = busyId === row.id;
                  const isPaid = row.status === 'paid';
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/20 transition align-top">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{nameFor(row)}</div>
                        {row.submittedByEmail && (
                          <div className="text-xs text-slate-500 truncate max-w-[180px]">{row.submittedByEmail}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 max-w-[320px]">
                        <div className="font-semibold text-slate-900">{row.title}</div>
                        <div className="text-xs text-slate-500">
                          {row.category}{row.vendor ? ` · ${row.vendor}` : ''}
                        </div>
                        {row.notes && (
                          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-600 bg-slate-50 border border-slate-150 rounded-md px-2 py-1.5">
                            <FileText size={12} className="text-slate-400 shrink-0 mt-0.5" />
                            <span className="whitespace-pre-wrap break-words">{row.notes}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <AmountCell {...amountProps(row)} />
                      </td>
                      <td className="py-3 px-4">
                        <span className={`sh-badge ${statusClass[row.status] || 'sh-badge-outline'}`}>{row.status || 'pending'}</span>
                        {isPaid && row.paidAt && (
                          <div className="text-[10px] text-slate-400 mt-1 font-mono">
                            {new Date(row.paidAt).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <AttachmentLinks attachments={row.attachments} emptyLabel="No proof" />
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500 font-mono whitespace-nowrap">{row.date || '—'}</td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end">
                          <InvoiceActions
                            row={row}
                            busy={busy}
                            onApprove={() => changeStatus(row, 'approved')}
                            onPay={() => changeStatus(row, 'paid', { needsConfirm: true })}
                            onDispute={() => changeStatus(row, 'disputed')}
                            onRevert={() => changeStatus(row, 'pending')}
                            onDelete={() => del(row)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
